import { InputGroup } from '@/components/ui/input-group';
import { Input, JsxStyleProps } from '@chakra-ui/react';
import { debounce } from 'lodash';
import { forwardRef, useEffect, useImperativeHandle, useMemo, useRef } from 'react';
import { MdClose, MdOutlineSearch } from 'react-icons/md';

export type SearchInputRef = {
  resetQuery: () => void;
};

export type SearchInputProps = {
  placeholder?: string;
  query: string;
  setQuery: (query: string) => void;
  styles?: JsxStyleProps;
};

const SearchInput = forwardRef<SearchInputRef, SearchInputProps>(
  ({ placeholder = 'Search', query, setQuery, styles }, ref) => {
    const localRef = useRef<HTMLInputElement | null>(null);
    const setQueryRef = useRef(setQuery);
    setQueryRef.current = setQuery;

    const debouncedSetQuery = useMemo(
      () =>
        debounce((value: string) => {
          setQueryRef.current(value);
        }, 200),
      []
    );

    useEffect(() => () => debouncedSetQuery.cancel(), [debouncedSetQuery]);

    const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (event.key === ' ') {
        event.stopPropagation(); // prevent parent from closing
      }

      if (event.key === 'Enter') {
        event.preventDefault();
        debouncedSetQuery.cancel();
        const inputValue = (event.target as HTMLInputElement).value;
        setQuery(inputValue);
      }
    };

    const handleQueryReset = () => {
      debouncedSetQuery.cancel();
      if (localRef.current) {
        localRef.current.value = '';
      }
      setQuery('');
    };

    // Sync external query into the input only when it isn't focused — otherwise a
    // debounced parent update (e.g. URL search_query) overwrites letters typed since.
    useEffect(() => {
      if (!localRef.current || localRef.current.value === query) return;
      if (document.activeElement === localRef.current) return;
      localRef.current.value = query;
    }, [query]);

    useImperativeHandle(ref, () => ({
      resetQuery: handleQueryReset,
    }));

    const { width: inputWidth = '100%' } = styles || {};

    return (
      <InputGroup
        {...styles}
        width={{ base: inputWidth as number | string, md: inputWidth as number | string, sm: '220px' }}
        startElement={<MdOutlineSearch size="20px" color="#8A8E9F" />}
        endElement={!!query && <MdClose size="20px" style={{ cursor: 'pointer' }} onClick={handleQueryReset} />}
      >
        <Input
          ref={localRef}
          name="global-search-input"
          placeholder={placeholder}
          defaultValue={query}
          onKeyDown={handleKeyDown}
          onChange={event => debouncedSetQuery(event.target.value)}
          required={false}
          autoComplete="off"
        />
      </InputGroup>
    );
  }
);

SearchInput.displayName = 'SearchInput';

export default SearchInput;
